import React, { useState } from 'react'
import { Box, Button, FormControl, FormLabel, Input, InputGroup, InputLeftElement, InputRightElement, VStack, Alert, AlertIcon, Text, Card, CardBody, Heading, HStack, Progress } from '@chakra-ui/react'
import { CheckCircle, XCircle, Eye, EyeOff, Lock } from 'lucide-react' 
import { useNavigate, useSearchParams } from 'react-router-dom'
import apiClient from '@/lib/api/client'
import { useThemeColors } from '@/hooks/useThemeColors'
import { useSiteSettings } from '@/hooks/useSiteSettings'
import { checkPasswordStrength } from '@/lib/utils/password' 

export default function CreatePassword() {
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  const client = searchParams.get('client') || ''
  const userId = searchParams.get('userId') || ''
  const colors = useThemeColors()
  const { settings: siteSettings } = useSiteSettings()
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirm, setShowConfirm] = useState(false)

  const evaluateRules = (pw: string) => {
    const minLength = 12
    const commonPasswords = ['password', '123456', '123456789', 'qwerty', 'abc123', 'password123', 'admin', 'letmein', 'welcome', 'monkey']
    return {
      length: pw.length >= minLength,
      uppercase: /[A-Z]/.test(pw),
      lowercase: /[a-z]/.test(pw),
      number: /[0-9]/.test(pw),
      symbol: /[^A-Za-z0-9]/.test(pw),
      notCommon: !commonPasswords.includes(pw.toLowerCase()),
    }
  }

  const onSubmit = async (e: React.FormEvent) => { 
    e.preventDefault()
    setError('')
    if (password !== confirm) {
      setError('Passwords do not match')
      return
    }

    const strength = checkPasswordStrength(password)
    if (!strength.is_strong) {
      setError(strength.feedback.join(', '))
      return
    }

    setLoading(true)
    try {
      await apiClient.post('/set-password', { user_id: userId, password, password_confirmation: confirm })
      // On success, redirect to login with client pre-filled and indicate setup success
      window.location.href = `/login?client=${encodeURIComponent(client)}&setup=success`
    } catch (err: any) {
      setError(err.response?.data?.errors?.join(', ') || err.response?.data?.message || 'Unable to set password')
    } finally {
      setLoading(false)
    }
  }

  return (
    <Box minH="100vh" bg={colors.bg} display="flex" alignItems="center" justifyContent="center">
      <Card maxW="md" w="full" bg={colors.cardBg}>
        <CardBody p={6}>
          <VStack spacing={4}>
            <Box textAlign="center">
              <Box mb={2} display="flex" justifyContent="center">
                <img src={siteSettings?.auth_logo || '/assets/img/als-logo-2.png'} alt="ActionLabs" style={{ maxWidth: '300px' }} />
              </Box>

              <Heading size="lg" color={colors.heading} mb={1}>Create your account password</Heading>
              {client && <Text color={colors.text} fontSize="sm">Setting password for <strong>{client}</strong></Text>}
            </Box>

            <Box as="form" onSubmit={onSubmit} w="100%">
              <VStack spacing={3}>
                {error && (
                  <Alert status="error" borderRadius="md">
                    <AlertIcon />
                    {error}
                  </Alert>
                )}

                <FormControl isRequired>
                  <FormLabel>Password</FormLabel>

                  <InputGroup>
                    <InputLeftElement pointerEvents="none">
                      <Lock size={18} />
                    </InputLeftElement>
                    <Input
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      pl="10"
                    />
                    <InputRightElement width="3rem">
                      <Button variant="ghost" size="sm" onClick={() => setShowPassword(!showPassword)} type="button">
                        {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                      </Button>
                    </InputRightElement>
                  </InputGroup>

                  {password && (() => {
                    const s = checkPasswordStrength(password)
                    const percent = Math.round((s.passed_checks / s.required_checks) * 100)
                    const rules = evaluateRules(password)
                    return (
                      <Box mt={2}>
                        <Text fontSize="sm" mb={1}>Strength: {s.passed_checks}/{s.required_checks}</Text>
                        <Progress value={percent} size="sm" colorScheme={s.is_strong ? 'green' : 'orange'} />

                        <VStack align="start" spacing={1} mt={2}>
                          <HStack>
                            {rules.length ? <CheckCircle color="green" /> : <XCircle color="gray" />} <Text fontSize="sm">At least 12 characters</Text>
                          </HStack>
                          <HStack>
                            {rules.uppercase ? <CheckCircle color="green" /> : <XCircle color="gray" />} <Text fontSize="sm">Uppercase letter</Text>
                          </HStack>
                          <HStack>
                            {rules.lowercase ? <CheckCircle color="green" /> : <XCircle color="gray" />} <Text fontSize="sm">Lowercase letter</Text>
                          </HStack>
                          <HStack>
                            {rules.number ? <CheckCircle color="green" /> : <XCircle color="gray" />} <Text fontSize="sm">Number</Text>
                          </HStack>
                          <HStack>
                            {rules.symbol ? <CheckCircle color="green" /> : <XCircle color="gray" />} <Text fontSize="sm">Special character</Text>
                          </HStack>
                          <HStack>
                            {rules.notCommon ? <CheckCircle color="green" /> : <XCircle color="gray" />} <Text fontSize="sm">Not a common password</Text>
                          </HStack>

                          {s.feedback.length > 0 && (
                            <Box mt={2}>
                              {s.feedback.map((fb, idx) => (
                                <Text key={idx} fontSize="sm" color="red.500">• {fb}</Text>
                              ))}
                            </Box>
                          )}
                        </VStack>
                      </Box>
                    )
                  })()}
                </FormControl>

                <FormControl isRequired>
                  <FormLabel>Confirm Password</FormLabel>
                  <InputGroup>
                    <Input
                      type={showConfirm ? 'text' : 'password'}
                      value={confirm}
                      onChange={(e) => setConfirm(e.target.value)}
                    />
                    <InputRightElement width="3rem">
                      {confirm ? (confirm === password ? <CheckCircle color="green" /> : <XCircle color="red" />) : null}
                    </InputRightElement>
                  </InputGroup>
                </FormControl>

                <Button
                  type="submit"
                  colorScheme="blue"
                  isLoading={loading}
                  isDisabled={loading || !checkPasswordStrength(password).is_strong || password !== confirm || !password}
                >
                  Continue
                </Button>

                <Button variant="link" onClick={() => navigate('/client-check')}>Back</Button>
              </VStack>
            </Box>
          </VStack>
        </CardBody>
      </Card>
    </Box>
  )
}
